import { inject, Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable, ReplaySubject } from "rxjs";
import { filter, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { Market } from "../types";
import { PCP_URL } from "../types/constants/appconstants";
import { AuthService } from "./data/auth.service";
import { OrganizationService } from "./data/organization.service";
import { UnsubscribeOnDestroy } from "../classes";

@Injectable({
	"providedIn": "root"
})
export class OrgSelectionService extends UnsubscribeOnDestroy {
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private orgService = inject(OrganizationService);
	private authService = inject(AuthService);
	private pcpBaseUrl = inject(PCP_URL);
	private selectedMarketSubject: ReplaySubject<Market> = new ReplaySubject<Market>();
	private currentMarket: string;

	get selectedMarket(): Observable<Market> {
		return this.selectedMarketSubject.asObservable();
	}

	constructor() {
		super();

		this.selectedMarketSubject
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((m) => {
				if (m) {
					this.currentMarket = m.sourceKey;
				}
			});

		const orgIDParam = this.getOrgIDParam();
		if (!orgIDParam && localStorage.getItem("selectedMarket") && localStorage.getItem("selectedMarket") !== "undefined") {
			try {
				const parsed = JSON.parse(localStorage.getItem("selectedMarket")!) as Market;
				if (parsed?.sourceKey) {
					this.selectedMarketSubject.next(parsed);
				}
			} catch {
				// ignore invalid stored market
			}
		} else {
			this.authService.isUserLoggedIn()
				.pipe(
					filter((loggedIn) => loggedIn),
					take(1),
					switchMap(() => this.authService.whenAccessTokenReady()),
					switchMap(() => this.orgService.getActiveMarkets()),
					takeUntil(this.ngUnsubscribe$)
				)
				.subscribe((mkts) => {
					if (orgIDParam) {
						const next = mkts.find((mkt) => mkt.sourceKey === orgIDParam);
						if (next) {
							this.selectedMarketSubject.next(next);
						}
					} else if (mkts[0]) {
						this.selectedMarketSubject.next(mkts[0]);
					}
				});
		}

		this.router.events.pipe(
			takeUntil(this.ngUnsubscribe$),
			filter((e) => e instanceof NavigationEnd),
			switchMap(() =>
				this.authService.isUserLoggedIn().pipe(
					filter((loggedIn) => loggedIn),
					take(1),
					switchMap(() => this.authService.whenAccessTokenReady()),
					switchMap(() => this.orgService.getActiveMarkets())
				)
			)
		)
			.subscribe((orgs) => {
				const orgIdParam = this.getOrgIDParam();

				if (orgIdParam && orgIdParam !== this.currentMarket) {
					const next = orgs.find((o) => o.sourceKey === orgIdParam);
					if (next) {
						this.selectedMarketSubject.next(next);
						localStorage.setItem("selectedMarket", JSON.stringify(next));
					}
				}
			});
	}

	resetSelectedMarket$() {
		return this.authService.whenAccessTokenReady().pipe(
			take(1),
			switchMap(() => this.orgService.getActiveMarkets()),
			takeUntil(this.ngUnsubscribe$),
			tap(mkts => {
				const next = mkts.find(mkt => mkt.sourceKey === this.currentMarket);
				if (next) {
					this.selectedMarketSubject.next(next);
					localStorage.setItem("selectedMarket", JSON.stringify(next));
				}
			})
		);
	}



	selectMarket(market: string | Market): void {
		//Emit a new market and update local storage
		if (typeof market === "string") {
			this.authService.whenAccessTokenReady()
				.pipe(
					take(1),
					switchMap(() => this.orgService.getActiveMarkets()),
					takeUntil(this.ngUnsubscribe$)
				)
				.subscribe((mkts) => {
					const org = mkts.find((mkt) => mkt.sourceKey === market);
					if (org) {
						this.selectedMarketSubject.next(org);
						localStorage.setItem("selectedMarket", JSON.stringify(org));
					}
				});
		} else {
			this.selectedMarketSubject.next(market);
			localStorage.setItem("selectedMarket", JSON.stringify(market));
		}

		//Redirect to the main page of the module
		if (location.href.indexOf("/portal/") > -1) {
			window.location.href = this.pcpBaseUrl + "/portal";
		}

		if (location.href.indexOf("/schedules/") > -1) {
			window.location.href = this.pcpBaseUrl + "/schedules";
		}

		if (location.href.indexOf("/recordables/") > -1) {
			window.location.href = this.pcpBaseUrl + "/recordables";
		}

		if (location.href.indexOf("/purchaseorders/") > -1) {
			window.location.href = this.pcpBaseUrl + "/purchaseorders";
		}
	}

	private getOrgIDParam(): string {
		let snapshot = this.router.routerState.snapshot.root;
		do {
			const orgIdParam = snapshot.paramMap.get("orgId");
			if (orgIdParam) {
				return orgIdParam;
			}
			snapshot = snapshot.children.find((c) => c.outlet === "primary");
		} while (snapshot);
		return null;
	}
}
